/**
 * Route Editor Panel Component
 * Panel for editing a single route with drag-and-drop stops
 */

import { useState, useCallback } from 'react';
import { Card, Button, Badge, getStatusBadgeVariant } from '../ui';
import DraggableStopsList from './DraggableStopsList';
import { User, MapPin, Clock, Navigation, Save, Undo, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { Route, RouteStop } from '../../types';
import { useCan } from '../../contexts/useAuth';

interface RouteEditorPanelProps {
  route: Route;
  stops: RouteStop[];
  onReorderStops: (routeId: string, newOrder: string[]) => Promise<void>;
  onRemoveStop?: (routeId: string, taskUid: string) => Promise<void>;
  onStopClick?: (stop: RouteStop) => void;
  selectedStopId?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export default function RouteEditorPanel({
  route,
  stops,
  onReorderStops,
  onRemoveStop,
  onStopClick,
  selectedStopId,
  isExpanded = true,
  onToggleExpand,
}: RouteEditorPanelProps) {
  const can = useCan();
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}m`;
    return `${hours}h ${mins}m`;
  };

  const handleReorder = useCallback((newOrder: string[]) => {
    setPendingOrder(newOrder);
  }, []);

  const handleSave = useCallback(async () => {
    if (!pendingOrder) return;
    
    setIsSaving(true);
    try {
      await onReorderStops(route._id, pendingOrder);
      setPendingOrder(null);
    } catch (error) {
      console.error('Failed to save order:', error);
    } finally {
      setIsSaving(false);
    }
  }, [pendingOrder, route._id, onReorderStops]);

  const handleUndo = useCallback(() => {
    setPendingOrder(null);
  }, []);

  const handleRemoveStop = useCallback(async (taskUid: string) => {
    if (!onRemoveStop) return;
    await onRemoveStop(route._id, taskUid);
  }, [route._id, onRemoveStop]);

  // Apply pending order to stops for display
  const displayStops = pendingOrder
    ? pendingOrder.map(uid => stops.find(s => s.task_uid === uid)).filter(Boolean) as RouteStop[]
    : stops;

  const hasChanges = pendingOrder !== null;

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Header */}
      <div
        className={cn(
          'px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors',
          hasChanges && 'bg-yellow-50 border-yellow-200'
        )}
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <User className="h-4 w-4 text-gray-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {route.driver_name || route.driver_uid}
                </span>
                <Badge variant={getStatusBadgeVariant(route.status)} size="sm">
                  {route.status}
                </Badge>
                {hasChanges && (
                  <Badge variant="warning" size="sm">
                    Unsaved
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {route.stop_count} stops
                </span>
                <span className="flex items-center gap-1">
                  <Navigation className="h-3 w-3" />
                  {route.total_distance_km.toFixed(1)} km
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDuration(route.google_eta_total_min || route.total_duration_min)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasChanges && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUndo();
                  }}
                  leftIcon={<Undo className="h-4 w-4" />}
                >
                  Undo
                </Button>
                {can('routes.edit') && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    loading={isSaving}
                    leftIcon={<Save className="h-4 w-4" />}
                  >
                    Save
                  </Button>
                )}
              </>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Stops List */}
      {isExpanded && (
        <div className="p-4 max-h-96 overflow-y-auto">
          <DraggableStopsList
            stops={displayStops}
            onReorder={handleReorder}
            onRemoveStop={onRemoveStop ? handleRemoveStop : undefined}
            onStopClick={onStopClick}
            selectedStopId={selectedStopId}
            isReordering={isSaving}
          />
        </div>
      )}
    </Card>
  );
}
